import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { products } from "@/data/products";

export interface CartItem {
  id: string;
  name: string;
  price: number;
  image: string;
  quantity: number;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (item: Omit<CartItem, "quantity">, quantity?: number) => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
  loading: boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const getLocalCart = (): CartItem[] => {
  const saved = localStorage.getItem("bunnie-cart");
  return saved ? JSON.parse(saved) : [];
};

const setLocalCart = (items: CartItem[]) => {
  localStorage.setItem("bunnie-cart", JSON.stringify(items));
};

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(getLocalCart);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  // Sync from DB when user logs in
  useEffect(() => {
    if (user) {
      syncFromDb();
    } else {
      setItems(getLocalCart());
    }
  }, [user]);

  // Save to localStorage whenever items change
  useEffect(() => {
    setLocalCart(items);
  }, [items]);

  const syncFromDb = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("cart_items")
        .select("product_id, quantity")
        .eq("user_id", user.id);

      if (error) throw error;

      // Merge local cart with DB cart
      const localItems = getLocalCart();
      const dbItems: CartItem[] = (data || []).map(row => {
        const product = products.find(p => p.id === row.product_id);
        return product ? {
          id: product.id,
          name: product.name,
          price: product.price,
          image: product.image,
          quantity: row.quantity,
        } : null;
      }).filter(Boolean) as CartItem[];

      // Merge: DB items take priority, add any local-only items
      const merged = [...dbItems];
      for (const localItem of localItems) {
        if (!merged.find(m => m.id === localItem.id)) {
          merged.push(localItem);
          // Also save to DB
          await supabase.from("cart_items").upsert({
            user_id: user.id,
            product_id: localItem.id,
            quantity: localItem.quantity,
          });
        }
      }
      setItems(merged);
    } catch (err) {
      console.error("Cart sync error:", err);
    }
    setLoading(false);
  };

  const addToCart = useCallback((item: Omit<CartItem, "quantity">, quantity = 1) => {
    setItems(prev => {
      const existing = prev.find(i => i.id === item.id);
      const newItems = existing
        ? prev.map(i => i.id === item.id ? { ...i, quantity: i.quantity + quantity } : i)
        : [...prev, { ...item, quantity }];

      toast.success(existing ? `Updated ${item.name} quantity` : `${item.name} added to cart!`);

      // Sync to DB
      if (user) {
        const newQty = existing ? existing.quantity + quantity : quantity;
        supabase.from("cart_items").upsert({
          user_id: user.id,
          product_id: item.id,
          quantity: newQty,
        }).then();
      }

      return newItems;
    });
  }, [user]);

  const removeFromCart = useCallback((id: string) => {
    setItems(prev => {
      const item = prev.find(i => i.id === id);
      if (item) toast(`${item.name} removed from cart`);
      if (user) {
        supabase.from("cart_items").delete().eq("user_id", user.id).eq("product_id", id).then();
      }
      return prev.filter(i => i.id !== id);
    });
  }, [user]);

  const updateQuantity = useCallback((id: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(id);
      return;
    }
    setItems(prev => prev.map(i => i.id === id ? { ...i, quantity } : i));
    if (user) {
      supabase.from("cart_items").upsert({ user_id: user.id, product_id: id, quantity }).then();
    }
  }, [user, removeFromCart]);

  const clearCart = useCallback(() => {
    setItems([]);
    toast("Cart cleared");
    if (user) {
      supabase.from("cart_items").delete().eq("user_id", user.id).then();
    }
  }, [user]);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addToCart, removeFromCart, updateQuantity, clearCart, totalItems, totalPrice, loading }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
